import React from "react";
import { Link } from "react-router-dom";

import { visibleModuleGroups } from "@/nav";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/auth/useAuth";
import { usePortalConfig } from "@/portal/usePortalConfig";

/** Главная: модули ЭТОЙ инсталляции, на которые у пользователя есть права.
 *
 * Берём те же пункты и те же блоки, что и меню (`nav.ts::MODULE_GROUPS`) —
 * карточка модуля и пункт меню не должны расходиться в маршруте, иконке или
 * блоке. Модуль отличается от служебной страницы наличием описания; блок без
 * видимых карточек не показывается. */
export function DashboardPage(): React.ReactElement {
  const { user } = useAuth();
  const { hasPage, title } = usePortalConfig();

  const groups = visibleModuleGroups((item) => {
    if (!item.description) return false;
    const page = item.page ?? item.permission;
    if (page && !hasPage(page)) return false;
    return !!user?.is_admin || (!!page && !!user?.permissions.includes(page));
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Здравствуйте, {user?.username}</h1>
        <p className="text-sm text-muted-foreground">{title}</p>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
          Доступных разделов нет — попросите администратора выдать права.
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.title} aria-label={group.title} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {group.title}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {group.items.map((item) => (
                <Link key={item.to} to={item.to} className="block">
                  <Card className="h-full transition-colors hover:bg-accent/30">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <item.icon className="size-5 shrink-0" />
                        {item.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      {item.description}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
